// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onSelectProduct: (productInfo: ProductInfo, product: ProductModel) => void;
    skipInventoryValidation?: boolean;
    selectButtonTitle?: string;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        location: getLocation(state),
        pricing: state.commonActions["relatedProductPricing"],
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    reset,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: InjectableCss;
    searchWrapper?: InjectableCss;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const productSelectorStyles: ProductSelectorStyles = {
    container: {
        css: css`
            width: 100%;
        `,
    },
    searchWrapper: {
        css: css`
            max-width: 300px;
            @media (max-width: 600px) {
                max-width: 100%;
            }
        `,
    },
    searchDynamicDropdown: {
        cssOverrides: {
            inputSelect: css`
                padding-left: 35px !important;
            `,
            selectedText: css`
                padding-left: 35px !important;
            `,
        },
        iconProps: {
            src: "Search",
            size: 18,
            color: "secondary",
            css: css`
                left: 10px;
                &&&& {
                    padding: 0;
                    top: 50%;
                    transform: translateY(-50%);
                }
                &&& svg path {
                    fill: ${getColor("secondary")};
                }
            `,
        },
        mergeCss: true,
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        cloudinaryWidth: "50",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: fontConfig.fontSize.small,
    },
    autocompleteErpText: {
        size: fontConfig.fontSize.small,
        css: css`
            margin-top: 5px;
        `,
    },
};

const ENTER_KEY = 13;

const RelatedProductSelector = ({
    onSelectProduct,
    skipInventoryValidation,
    searchProducts,
    isSearching,
    searchResults,
    setProduct,
    clearProduct,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    location,
    pricing,
    reset,
}: Props) => {
    const [qty, setQty] = useState("1");
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [selectedProductId, setSelectedProductId] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));

    useEffect(() => {
        clearProduct();
        return () => {
            clearProduct();
        };
    }, []);

    useEffect(() => {
        reset();
    }, [location.pathname]);

    useEffect(() => {
        if (!searchResults || !searchTerm) {
            return;
        }

        const newOptions = searchResults.map(product => ({
            optionText: product.title,
            optionValue: product.id || undefined,
            rowChildren: (
                <StyledWrapper key={`relatedProduct_${product.id}`} {...styles.optionWrapper}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage
                            {...styles.productImage}
                            altText={product.title || product.name}
                            src={product.image}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoWrapper}>
                        <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                        <Typography {...styles.autocompleteErpText}>{product.erpNumber}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));
        setOptions(newOptions);
    }, [searchResults]);

    useEffect(() => {
        if (searchTerm || selectedProductId) {
            setErrorMessage(customErrorMessage);
        }
    }, [customErrorMessage]);

    useEffect(() => {
        setSelectedProductId(selectedProductInfo ? selectedProductInfo.productId : "");
        if (selectedProductInfo && product && pricing?.hasOwnProperty(product.id)) {
            selectProduct();
        }
    }, [selectedProductInfo, pricing]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(productIsConfigurableMessage || translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(productIsUnavailableMessage || translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
    }, [errorType]);

    const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    loadPricing: true,
                } as any);
            } else {
                setProduct({
                    productId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    loadPricing: true,
                } as any);
            }
        }
    };

    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ query });
    }, 300);

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        setSearchTerm(event.target.value);

        debouncedSearchProducts(event.target.value);
    };

    const selectProduct = () => {
        if (
            !selectedProductInfo ||
            errorMessage ||
            !product ||
            !pricing?.hasOwnProperty(product.id) ||
            !pricing[product.id]
        ) {
            return;
        }
        const productPricing = pricing[product.id];
        const newProductInfo = { ...selectedProductInfo, pricing: productPricing };
        newProductInfo.qtyOrdered = Math.max(Number(qty) || 1, product.minimumOrderQty || 1);
        onSelectProduct(newProductInfo, product);
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            if (selectedProductInfo) {
                selectProduct();
            } else if (searchTerm && (!searchResults || searchResults.length === 0)) {
                setProduct({ searchTerm, skipInventoryValidation });
            }
        }
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="realtedProductSelector">
            <StyledWrapper {...styles.searchWrapper}>
                <DynamicDropdown
                    {...styles.searchDynamicDropdown}
                    label=""
                    onSelectionChange={onSelectionChangeHandler}
                    onInputChange={onInputChanged}
                    onKeyPress={onKeyPress}
                    filterOption={() => true}
                    selected={selectedProductId}
                    placeholder={translate("Search")}
                    isLoading={isSearching}
                    options={options}
                    error={errorMessage}
                    data-test-selector="realtedProductSelector_search"
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RelatedProductSelector);
